import React from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Avatar,
  Breadcrumbs,
  Link,
  Paper,
} from "@mui/material";
import {
  Call as CallIcon,
  Email as EmailIcon,
  ChatBubbleOutline as CommentIcon,
  MoreHoriz as MoreIcon,
  ArrowBack as BackIcon,
  Business as DesignationIcon,
  CalendarMonth as DateIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router";
import { themeColors } from "../../app/layout/theme";

export const EntityDetailLayout: React.FC<{
  header: React.ReactNode;
  sidebar: React.ReactNode;
  main: React.ReactNode;
}> = ({ header, sidebar, main }) => (
  <Box
    sx={{ bgcolor: "#f5f6fa", minHeight: "100vh", pb: 4, overflowY: "auto" }}
  >
    {header}
    <Box sx={{ px: 3, pt: 2, display: "flex", gap: 2.5, alignItems: "start" }}>
      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {sidebar}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>{main}</Box>
    </Box>
  </Box>
);

// ── Header ────────────────────────────────────────────────────────────────────

interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface EntityDetailHeaderProps {
  title: string;
  initials: string;
  breadcrumbs: BreadcrumbItem[];
  designation?: string;
  sinceDate?: string;
  onCall?: () => void;
  onEmail?: () => void;
  onComment?: () => void;
}

interface EntityOwnerCardProps {
  ownerName: string;
  label?: string;
  initials?: string;
  avatarColor?: string;
  onClick?: () => void;
  actionIcon?: React.ReactNode;
}

export const EntityDetailHeader: React.FC<EntityDetailHeaderProps> = ({
  title,
  initials,
  breadcrumbs,
  designation,
  sinceDate,
  onCall,
  onEmail,
  onComment,
}) => (
  <Box
    sx={{
      px: 3,
      pt: 2.5,
      pb: 2,
      bgcolor: "#fff",
      borderBottom: `1px solid ${themeColors.border}`,
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      <IconButton
        size="small"
        component={RouterLink}
        to={breadcrumbs[breadcrumbs.length - 2]?.to || "#"}
      >
        <BackIcon sx={{ fontSize: 18 }} />
      </IconButton>
      <Breadcrumbs separator={<Typography color="text.disabled">/</Typography>}>
        {breadcrumbs.map((item, idx) =>
          item.to ? (
            <Link
              key={idx}
              component={RouterLink}
              to={item.to}
              underline="hover"
              color="inherit"
              sx={{ fontSize: "13px", color: "text.secondary" }}
            >
              {item.label}
            </Link>
          ) : (
            <Typography key={idx} sx={{ fontSize: "13px", fontWeight: 600 }}>
              {item.label}
            </Typography>
          ),
        )}
      </Breadcrumbs>
    </Stack>

    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar
          sx={{
            width: 64,
            height: 64,
            bgcolor: "#f0f0f5",
            color: themeColors.primary,
            fontWeight: 700,
            fontSize: "22px",
            border: `1px solid ${themeColors.border}`,
          }}
        >
          {initials}
        </Avatar>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={2.5}>
            {designation && (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <DesignationIcon
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
                <Typography variant="body2" color="text.secondary">
                  {designation}
                </Typography>
              </Stack>
            )}
            {sinceDate && (
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <DateIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  Contact since {sinceDate}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1.5}>
        <Button
          variant="outlined"
          startIcon={<CallIcon />}
          onClick={onCall}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            color: "text.primary",
            borderColor: themeColors.border,
          }}
        >
          Call
        </Button>
        <Button
          variant="outlined"
          startIcon={<EmailIcon />}
          onClick={onEmail}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            color: "text.primary",
            borderColor: themeColors.border,
          }}
        >
          Email
        </Button>
        <Button
          variant="outlined"
          startIcon={<CommentIcon />}
          onClick={onComment}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            color: "text.primary",
            borderColor: themeColors.border,
          }}
        >
          Comment
        </Button>
        <IconButton
          sx={{
            border: `1px solid ${themeColors.border}`,
            borderRadius: "8px",
          }}
        >
          <MoreIcon />
        </IconButton>
      </Stack>
    </Stack>
  </Box>
);

// ── Components ───────────────────────────────────────────────────────────────

export const InfoRow: React.FC<{
  icon: React.ReactNode;
  label?: string;
  value: React.ReactNode;
  action?: React.ReactNode;
}> = ({ icon, label, value, action }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, py: 0.6 }}>
    <Box sx={{ color: "#9ca3af", flexShrink: 0, display: "flex", mt: "1px" }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {label && (
        <Typography
          fontSize="10.5px"
          color="text.secondary"
          fontWeight={500}
          lineHeight={1.3}
        >
          {label}
        </Typography>
      )}
      <Box sx={{ fontSize: "12.5px", wordBreak: "break-word" }}>
        {value}
      </Box>
    </Box>
    {action}
  </Box>
);

export const SummaryRow: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 0.5,
    }}
  >
    <Typography fontSize="12.5px" color="text.secondary" fontWeight={500}>
      {label}
    </Typography>
    <Typography fontSize="12.5px" fontWeight={700} color="text.primary">
      {value}
    </Typography>
  </Box>
);

export const EntityOwnerCard: React.FC<EntityOwnerCardProps> = ({
  ownerName,
  initials,
  avatarColor = "#C7D2FE",
  onClick,
  actionIcon,
}: EntityOwnerCardProps) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: "12px",
        border: `1px solid ${themeColors.border}`,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.2s",
        "&:hover": onClick
          ? { borderColor: themeColors.primary, bgcolor: "#fafafa" }
          : {},
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: "12.5px",
          fontWeight: 700,
          bgcolor: avatarColor,
          color: themeColors.primary,
          flexShrink: 0,
        }}
      >
        {initials ||
          ownerName
            .split(" ")
            .map((n) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase()}
      </Avatar>
      <Typography fontSize="14.5px" fontWeight={600} sx={{ flex: 1 }} noWrap>
        {ownerName}
      </Typography>
      {actionIcon}
    </Box>
  );
};
